import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import { InteractionObjectType, InteractionType } from "../../constants";
import { PillData } from "../../models";
import { searchInteractions } from "../../service/query";
import { useAuth } from "../../useAuth";
import { dataGridMapFilterCriteria } from "../../widget";

interface UseLandholdingWithTabsTemplateReturnData {
  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  shouldRefreshTags: () => void;
  tags: PillData[];
}

export const useLandholdingWithTabsTemplate = (
  landholdingUuid: string | undefined
): UseLandholdingWithTabsTemplateReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [tags, setTags] = useState<PillData[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);

  const shouldRefreshTags = (): void => setRefreshTags((prev) => !prev);

  const fetchTags = useCallback(async (objectUuid: string, organisationUuid: string): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = {
      operator: "eq",
      value: InteractionType.TAG,
    };
    filterCriteria.objectType = {
      operator: "eq",
      value: InteractionObjectType.ORGANISATION,
    };
    filterCriteria.objectUuid = {
      operator: "eq",
      value: organisationUuid,
    };
    filterCriteria.objectInteractions = {
      some: {
        objectUuid: {
          operator: "eq",
          value: objectUuid,
        },
        endDate: {
          operator: "eq",
          value: null,
        },
      },
    };
    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: filterCriteria },
    }).then((response) => {
      if (response.data?.results != null) {
        setTags(
          response.data.results.map(
            (interaction) =>
              ({
                key: interaction.uuid,
                label: JSON.parse(interaction.data).name,
              }) as PillData
          )
        );
      }
    });
  }, []);

  useEffect(() => {
    if (landholdingUuid && currentOrganisationUuid) {
      fetchTags(landholdingUuid, currentOrganisationUuid);
    }
  }, [fetchTags, landholdingUuid, currentOrganisationUuid, refreshTags]);

  return {
    showTagsPanel,
    setShowTagsPanel,
    shouldRefreshTags,
    tags,
  };
};
